import { IQuoteContacts, QuoteStatusEnum } from '@monetize/types/app';
import { TIMEZONE_CONFIG, toDateTimeShort } from '@monetize/utils/core';
import React, { useMemo } from 'react';
import { MdRefresh, MdSend } from 'react-icons/md';
import { getMessageFromError } from '../../../api/axios';
import { useGetEsignRecipients } from '../../../api/cpqService';
import { useGetTimezones } from '../../../api/timezoneService';
import { useQuoteResendEsign } from '../../../hooks/useQuoteResendEsign';
import {
  MBox,
  MCustomIconButton,
  MFlex,
  MSkeleton,
  MText,
  MTooltip,
} from '../../Monetize';
import { ESignRecipientItem } from '../ESignRecipientItem';

interface QuoteStepEsignRecipientsProps {
  quoteId: string;
  contacts: IQuoteContacts;
  quoteStatus: QuoteStatusEnum;
}

export const QuoteStepEsignRecipients = React.forwardRef<
  any,
  QuoteStepEsignRecipientsProps
>(({ quoteId, contacts, quoteStatus }: QuoteStepEsignRecipientsProps, ref) => {
  const {
    isLoading,
    data: esignRecipients,
    error,
    refetch,
    isRefetching,
    dataUpdatedAt,
  } = useGetEsignRecipients(quoteId, {
    refetchOnWindowFocus: false,
    gcTime: 1000 * 60 * 10, // 5 minutes
    placeholderData: (previousData, previousQuery) => previousData,
  });

  const { onResendQuote } = useQuoteResendEsign(quoteId);

  const { data: timeZones } = useGetTimezones();
  const timeZoneLabel =
    timeZones?.timezonesByValue[TIMEZONE_CONFIG.userTimezone]?.label ?? '';

  const lastUpdatedAt = useMemo(
    () =>
      toDateTimeShort(
        dataUpdatedAt ? new Date(dataUpdatedAt) : new Date(),
        'userTimezone',
      ),
    [dataUpdatedAt],
  );

  const errorMessage = useMemo(() => {
    if (!error) {
      return null;
    }
    return getMessageFromError(error, 'Failed to load eSign recipients');
  }, [error]);

  if (isLoading) {
    return <LoadingSkeleton contacts={contacts} />;
  }

  if (errorMessage) {
    return <MText color="tRed.base">{errorMessage}</MText>;
  }

  if (!esignRecipients) {
    return null;
  }

  // Ensure quote is set, no one has declined, and there is at least one signer pending
  const pendingSigners = esignRecipients.signers.filter(
    ({ status }) => status === 'sent',
  );
  const canResend =
    quoteStatus === QuoteStatusEnum.SENT &&
    pendingSigners.length > 0 &&
    esignRecipients.signers.every(({ declinedDateTime }) => !declinedDateTime);

  return (
    <MFlex w="100%" flexDirection="column">
      {!!esignRecipients.signers.length && (
        <MFlex justifyContent="space-between" alignItems="center">
          <MText fontWeight="600" fontSize="md">
            Signers
          </MText>
          <MBox>
            {canResend && (
              <MTooltip label={`Re-send email to current signers`}>
                <MCustomIconButton
                  btnSize={4}
                  variant="icon"
                  icon={MdSend}
                  aria-label="Re-send email to current signers"
                  isDisabled={isLoading}
                  onClick={() => onResendQuote(pendingSigners)}
                />
              </MTooltip>
            )}
            <MTooltip
              label={`Last updated at ${lastUpdatedAt} ${timeZoneLabel}`}
            >
              <MCustomIconButton
                btnSize={4}
                variant="icon"
                icon={MdRefresh}
                aria-label="Refresh recipients"
                isLoading={isRefetching}
                isDisabled={isRefetching}
                onClick={() => refetch()}
              />
            </MTooltip>
          </MBox>
        </MFlex>
      )}
      {esignRecipients.signers.map((recipient) => (
        <ESignRecipientItem
          key={recipient.id}
          recipient={recipient}
          timeZoneLabel={timeZoneLabel}
        />
      ))}
      {!!esignRecipients.ccContacts.length && (
        <MText fontWeight="600" fontSize="md" mt={2}>
          CC Recipients
        </MText>
      )}
      {esignRecipients.ccContacts.map((recipient) => (
        <ESignRecipientItem
          key={recipient.id}
          recipient={recipient}
          isCC
          timeZoneLabel={timeZoneLabel}
        />
      ))}
    </MFlex>
  );
});

const LoadingSkeleton = ({ contacts }: { contacts: IQuoteContacts }) => {
  return (
    <MBox padding="2" w="100%">
      <MFlex flexDirection="column" w="100%">
        <MSkeleton height="25px" width="50%" mt={2} />
        {contacts.esign.map((_, i) => (
          <MBox key={i} ml={2}>
            <MSkeleton height="21px" width="75%" mt={2} />
            <MFlex>
              <MSkeleton height="20px" width="16px" mt={2} mr={1} />
              <MSkeleton height="20px" width="80%" mt={2} />
            </MFlex>
          </MBox>
        ))}
      </MFlex>

      <MFlex flexDirection="column" w="100%" mt={2}>
        <MSkeleton height="25px" width="50%" mt={2} />
        <MBox ml={2}>
          <MSkeleton height="21px" width="75%" mt={2} />
          <MFlex>
            <MSkeleton height="20px" width="16px" mt={2} mr={1} />
            <MSkeleton height="20px" width="80%" mt={2} />
          </MFlex>
        </MBox>
      </MFlex>
    </MBox>
  );
};
